import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Gives every media the renderer used to centre a position and anchor that centre it.
 *
 * Until now a non-sticker media at position (0, 0) was centred on its visible region (its stencil
 * mask's bounds, or the frame) by the renderer. The renderer no longer centres anything: every
 * media puts its anchor point at its position, the way every other asset is placed. Each media at
 * (0, 0) is given a (0.5, 0.5) anchor at the centre of its visible region, so it is drawn exactly
 * where it was and keeps that centre when zoomed or when its source is swapped. A media whose old
 * anchor was not (0, 0) keeps that offset, measured with the same fit the renderer uses (declared
 * source size, fit box, fill mode, zoom).
 *
 * Any other media already placed its anchor point at its position, so it is drawn where it was
 * without changes. Stickers follow the same rule, and media laid out by an asset group never read
 * their position, so none of those is touched.
 */

type Size = { width: number; height: number };
type Rect = Size & { x: number; y: number };

const REFERENCE_RESOLUTIONS: Record<string, Size> = {
    landscape: { width: 1920, height: 1080 },
    square: { width: 1080, height: 1080 },
    vertical: { width: 1080, height: 1920 },
    fourfive: { width: 1080, height: 1350 },
};

const MEDIA_CLASS_TYPES = ['MediaAsset', 'TimeAwareMediaAsset'];

function rectFromPoints(points: number[], resolution: Size): Rect {
    const xs = points.filter((_value, index) => index % 2 === 0);
    const ys = points.filter((_value, index) => index % 2 === 1);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return {
        x: minX * resolution.width,
        y: minY * resolution.height,
        width: (Math.max(...xs) - minX) * resolution.width,
        height: (Math.max(...ys) - minY) * resolution.height,
    };
}

/**
 * The bounds the renderer reads off a stencil mask. Polygon bounds are combined the way the
 * renderer combines them — top-left of both, the larger of each extent — so the region measured
 * here is the one the media was actually centred on.
 */
function maskBounds(mask: Immutable.Map<string, unknown>, resolution: Size): Rect {
    const classType = mask.get('classType');
    if (classType === 'PolygonMask') {
        const start = rectFromPoints(
            (mask.get('startPoints') as Immutable.List<number>).toArray(),
            resolution,
        );
        const end = rectFromPoints(
            (mask.get('endPoints') as Immutable.List<number>).toArray(),
            resolution,
        );
        return {
            x: Math.min(start.x, end.x),
            y: Math.min(start.y, end.y),
            width: Math.max(start.width, end.width),
            height: Math.max(start.height, end.height),
        };
    }
    if (classType === 'CircleMask') {
        const centerX = (mask.get('centerX') as number | undefined) ?? resolution.width * 0.5;
        const centerY = (mask.get('centerY') as number | undefined) ?? resolution.height * 0.5;
        const radius = Math.max(
            (mask.get('startRadius') as number | undefined) ?? 0,
            (mask.get('endRadius') as number | undefined) ?? 0.5,
        );
        return {
            x: centerX - radius,
            y: centerY - radius,
            width: radius * 2,
            height: radius * 2,
        };
    }
    throw new Error(`Unknown mask type ${classType}`);
}

function fitSize(source: Size, target: Size, fillMode: string): Size {
    const widthRatio = target.width / source.width;
    const heightRatio = target.height / source.height;
    const ratio = (() => {
        switch (fillMode) {
            case 'cover':
                return Math.max(widthRatio, heightRatio);
            case 'contain':
                return Math.min(widthRatio, heightRatio);
            case 'area':
                return Math.sqrt((target.width * target.height) / (source.width * source.height));
            default:
                throw new Error(`invalid fillMode: ${fillMode}`);
        }
    })();
    return {
        width: Math.round(source.width * ratio),
        height: Math.round(source.height * ratio),
    };
}

function declaredSourceSize(asset: Immutable.Map<string, unknown>): Size | null {
    const sourceData = asset.get('sourceData') as Immutable.Map<string, unknown> | null | undefined;
    const width = sourceData?.get('width');
    const height = sourceData?.get('height');
    if (typeof width !== 'number' || typeof height !== 'number' || width <= 0 || height <= 0) {
        return null;
    }
    return { width, height };
}

/**
 * The size the media is drawn at: its source fitted to its fit box, times the user's zoom on a
 * cover fill. A media with no declared source size is measured as its fit box.
 */
function displaySize(asset: Immutable.Map<string, unknown>, fitBox: Size): Size {
    const source = declaredSourceSize(asset);
    if (!source) {
        return fitBox;
    }
    const fillMode = (asset.get('fillMode') as string | undefined) || 'cover';
    const fitted = fitSize(source, fitBox, fillMode);
    if (fillMode !== 'cover') {
        return fitted;
    }
    const matrix = asset.get('transformMatrix') as Immutable.List<number> | undefined;
    const scaleX = matrix?.get(0) ?? 1;
    const scaleY = matrix?.get(3) ?? 1;
    return { width: fitted.width * scaleX, height: fitted.height * scaleY };
}

function migrateMedia(
    map: Immutable.Map<string, unknown>,
    assetPath: Array<string | number>,
    resolution: Size,
) {
    const asset = map.getIn(assetPath) as Immutable.Map<string, unknown>;
    const positionPath = [...assetPath, 'transformation', 'position'];
    const anchorPath = [...assetPath, 'transformation', 'anchor'];
    const x = (map.getIn([...positionPath, 'x']) as number | undefined) ?? 0;
    const y = (map.getIn([...positionPath, 'y']) as number | undefined) ?? 0;
    const anchorX = (map.getIn([...anchorPath, 'x']) as number | undefined) ?? 0;
    const anchorY = (map.getIn([...anchorPath, 'y']) as number | undefined) ?? 0;
    if (x !== 0 || y !== 0) {
        return;
    }

    const mask = asset.get('mask') as Immutable.Map<string, unknown> | null | undefined;
    const scaleMultiplier = (asset.get('scaleMultiplier') as number | undefined) ?? 1;
    const visibleRegion = mask
        ? maskBounds(mask, resolution)
        : { x: 0, y: 0, width: resolution.width, height: resolution.height };
    const unscaledFitBox = mask
        ? visibleRegion
        : {
              width: (asset.get('maxWidth') as number | undefined) ?? resolution.width,
              height: (asset.get('maxHeight') as number | undefined) ?? resolution.height,
          };
    const fitBox = {
        width: unscaledFitBox.width * scaleMultiplier,
        height: unscaledFitBox.height * scaleMultiplier,
    };
    const size = displaySize(asset, fitBox);

    map.setIn(
        [...positionPath, 'x'],
        visibleRegion.x + visibleRegion.width / 2 - anchorX * size.width,
    );
    map.setIn(
        [...positionPath, 'y'],
        visibleRegion.y + visibleRegion.height / 2 - anchorY * size.height,
    );
    map.setIn([...anchorPath, 'x'], 0.5);
    map.setIn([...anchorPath, 'y'], 0.5);
}

function migrateAssets(
    map: Immutable.Map<string, unknown>,
    listPath: Array<string | number>,
    resolution: () => Size,
) {
    const assets = map.getIn(listPath) as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;
    assets?.forEach((asset, index) => {
        const isMedia = MEDIA_CLASS_TYPES.includes(asset.get('classType') as string);
        if (!isMedia || asset.get('createdFrom') === 'STICKER') {
            return;
        }
        migrateMedia(map, [...listPath, index], resolution());
    });
}

export const migrationTo9_245 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            map.get('slides').forEach((_scene: unknown, i: number) => {
                map.setIn(
                    ['slides', i],
                    this.migrateScene(map.getIn(['slides', i]) as Immutable.Map<string, unknown>),
                );
            });
        });
    },
    migrateScene(scene) {
        return scene.withMutations((map) => {
            const resolution = () => {
                const aspectRatio = map.get('aspectRatio') as string;
                const referenceResolution = REFERENCE_RESOLUTIONS[aspectRatio];
                if (!referenceResolution) {
                    throw new Error(`Unknown scene aspect ratio: ${aspectRatio}`);
                }
                return referenceResolution;
            };

            migrateAssets(map, ['children'], resolution);
            migrateAssets(map, ['stickers'], resolution);
            const subslides = map.get('subslides') as Immutable.List<unknown> | undefined;
            subslides?.forEach((_subslide, index) => {
                migrateAssets(map, ['subslides', index, 'children'], resolution);
            });
        });
    },
});
